import type { Question } from "components/Admin/Organizations/types/Module.type";
import * as SignalsModels from "components/Signals/SignalsModels";
import { compareAnswerOptionsByModuleOrder } from "models/AnswerType";
import { BaseStore } from "stores/BaseStore";
import type ChartCardStore from "../ChartCardStore";

type ChartOrderRuleTarget = "axis" | "slice";

/**
 * One thing being ordered, as the post-format stage knows it: the text it renders as, and the
 * identifier behind it when the chart row carried one.
 *
 * Most rules only look at the label. They order by volume, or against a list of expected values,
 * both of which are stated in the label's own terms. Continuous buckets are the exception and read
 * the identifier, because their labels are written to be read rather than sorted.
 */
export type OrderableBucket = {
    label: string;
    identifier?: string;
};

/**
 * Pairs a rendered label with its identifier, keeping the identifier only when it is one.
 *
 * Both places that build these read the identifier out of a structure that falls back to the label
 * when the chart carried none: Google's `{ v, f }` cell, and a series column header. Passing that
 * fallback through would have bucket ordering compare label text under another name, which sorts
 * "Feb 2026" ahead of "Jan 2026". Dropping it leaves the rows in the order the pre-render pass put
 * them in.
 */
export const toOrderableBucket = (
    label: string,
    identifier: string | undefined,
): OrderableBucket => ({
    label,
    identifier: identifier === label ? undefined : identifier,
});

class OrderRulesStore extends BaseStore {
    constructor(private chartStore: ChartCardStore) {
        super("Order Rules Store");
    }

    public getTransformContextOrderMaps(): {
        questionOrderMap?: Map<string, number>;
        questionResponseOrderMap?: Map<string, number>;
    } {
        return {
            questionOrderMap: this.buildQuestionOrderMap(),
            questionResponseOrderMap: this.buildQuestionResponseOrderMap(),
        };
    }

    /**
     * Returns the order rules that can be applied before chart rendering.
     * The chosen rule targets whichever dimension is on the x-axis: the chart's
     * default rule when not swapped, or the grouping catalog rule when swapped.
     * Line charts always sort by the slice rule so legend/colors remain stable.
     */
    public getResolvedPreRenderOrderRules(): SignalsModels.OrderRule[] {
        // Apply the stack/series rule first so the subsequent (stable) bar sort keeps it as the
        // secondary order: bars ordered by the bar rule, stacks ordered by the stack rule. Null for
        // line charts (their series are already ordered by the slice rule) or when no identifier.
        const stackRule = this.getStackOrderRule();
        const withStackRule = (rules: SignalsModels.OrderRule[]) =>
            stackRule ? [stackRule, ...rules] : rules;

        if (!this.chartStore.groupingStore.isGrouped) {
            const chartRule = this.getChartAxisOrderRule();
            return withStackRule(chartRule ? [chartRule] : []);
        }

        if (this.chartStore.chartType === "Line") {
            const sliceRule = this.withSliceVolumeAlphabeticalFallback(
                this.getGroupingOrderRule(),
            );
            return sliceRule ? [sliceRule] : [];
        }

        const barAxisRule = this.chartStore.groupingStore.swapGroupingAndX
            ? this.getGroupingAwareOrderRule("axis")
            : this.getChartAxisOrderRule();

        return withStackRule(barAxisRule ? [barAxisRule] : []);
    }

    /**
     * Resolves a stack/series order rule from the org order-rule catalog by the identifier the
     * endpoint config requests (e.g. "results"), applied to the grouping dimension. Lets grouped
     * charts order their stacks independently of the x-axis (bar) order rule. Not applied to line
     * charts (their series are already ordered by the slice rule).
     */
    private getStackOrderRule(): SignalsModels.OrderRule | null {
        const identifier = this.chartStore.config.stackOrderRuleIdentifier
            ?.trim()
            .toLowerCase();
        if (!identifier || this.chartStore.chartType === "Line") {
            return null;
        }

        const matchingOrderRule =
            this.chartStore.optionsStore.signalsOrderRuleCatalog.find(
                (item) =>
                    item.ruleIdentifier?.trim().toLowerCase() === identifier,
            );
        if (!matchingOrderRule) {
            return null;
        }

        return {
            orderByField: this.getGroupingOrderFieldByChartType(),
            orderByDirection: matchingOrderRule.orderByDirection,
            orderByValues: matchingOrderRule.orderByValues,
        };
    }

    public getFormattedSeriesOrderRule():
        | SignalsModels.OrderRule
        | null
        | undefined {
        return this.withSliceVolumeAlphabeticalFallback(
            this.chartStore.chartType === "Line" ||
                !this.chartStore.groupingStore.swapGroupingAndX
                ? this.getGroupingAwareOrderRule("slice")
                : this.getChartSliceOrderRule(),
        );
    }

    public usesFixedGroupingOrderRule(): boolean {
        return (
            this.chartStore.config.renderOptions.forceStacked &&
            !!this.chartStore.config.grouping.fixedGroupingOrderRule
                ?.groupByOption
        );
    }

    public getFormattedBucketOrderRule(
        swapGroupingAndX: boolean,
    ): SignalsModels.OrderRule | null | undefined {
        return swapGroupingAndX
            ? this.getGroupingAwareOrderRule("axis")
            : this.getChartAxisOrderRule();
    }

    public compareBucketsByOrderRule(
        left: OrderableBucket,
        right: OrderableBucket,
        orderRule: SignalsModels.OrderRule,
        bucketTotals?: Map<string, number>,
    ): number {
        const leftLabel = left.label;
        const rightLabel = right.label;

        if (orderRule.orderByValues === "volume") {
            if (!bucketTotals) {
                return 0;
            }

            const leftTotal = bucketTotals.get(leftLabel) ?? 0;
            const rightTotal = bucketTotals.get(rightLabel) ?? 0;

            return orderRule.orderByDirection === "ascending"
                ? leftTotal - rightTotal
                : rightTotal - leftTotal;
        }

        if (orderRule.orderByValues === "alphabetical") {
            return orderRule.orderByDirection === "ascending"
                ? leftLabel.localeCompare(rightLabel, undefined, {
                      sensitivity: "base",
                  })
                : rightLabel.localeCompare(leftLabel, undefined, {
                      sensitivity: "base",
                  });
        }

        if (orderRule.orderByValues === "bucketOrder") {
            return this.compareBuckets(left, right, orderRule.orderByDirection);
        }

        if (orderRule.orderByValues === "questionOrder") {
            return this.compareBucketLabelsByOrderMap(
                leftLabel,
                rightLabel,
                this.buildQuestionOrderMap(),
                orderRule.orderByDirection,
            );
        }

        if (orderRule.orderByValues === "questionResponseOrder") {
            return this.compareBucketLabelsByOrderMap(
                leftLabel,
                rightLabel,
                this.buildQuestionResponseOrderMap(),
                orderRule.orderByDirection,
            );
        }

        if (Array.isArray(orderRule.orderByValues)) {
            const orderedValues =
                orderRule.orderByDirection === "ascending"
                    ? [...orderRule.orderByValues].reverse()
                    : [...orderRule.orderByValues];
            const orderMap = new Map<string, number>();

            orderedValues.forEach((value, index) => {
                const normalizedValue = this.normalizeOrderingValue(value);
                if (!normalizedValue.length || orderMap.has(normalizedValue))
                    return;
                orderMap.set(normalizedValue, index);
            });

            const leftOrder =
                orderMap.get(this.normalizeOrderingValue(leftLabel)) ??
                Number.MAX_SAFE_INTEGER;
            const rightOrder =
                orderMap.get(this.normalizeOrderingValue(rightLabel)) ??
                Number.MAX_SAFE_INTEGER;

            return leftOrder - rightOrder;
        }

        return 0;
    }

    /**
     * Returns the grouping catalog order rule unless this endpoint is configured
     * to ignore it, in which case we use the chart's own order rule.
     */
    private getGroupingAwareOrderRule(
        target: ChartOrderRuleTarget,
    ): SignalsModels.OrderRule | null | undefined {
        if (this.chartStore.config.grouping.ignoreGroupingOrderRule) {
            return target === "slice"
                ? this.getChartSliceOrderRule()
                : this.getChartAxisOrderRule();
        }

        return this.getGroupingOrderRule();
    }

    private getChartAxisOrderRule():
        | SignalsModels.OrderRule
        | null
        | undefined {
        return this.withInferredChartOrderRuleField(
            this.chartStore.orderRule,
            "axis",
        );
    }

    private getChartSliceOrderRule():
        | SignalsModels.OrderRule
        | null
        | undefined {
        return this.withInferredChartOrderRuleField(
            this.chartStore.orderRule,
            "slice",
        );
    }

    private withInferredChartOrderRuleField(
        rule: SignalsModels.OrderRule | null | undefined,
        target: ChartOrderRuleTarget,
    ): SignalsModels.OrderRule | null | undefined {
        if (!rule || rule.orderByField) {
            return rule;
        }

        return {
            ...rule,
            orderByField: this.getInferredChartOrderField(target),
        };
    }

    private getInferredChartOrderField(target: ChartOrderRuleTarget): string {
        if (target === "slice") {
            return this.chartStore.chartType === "Line" ? "series" : "x";
        }

        if (
            this.chartStore.groupingStore.isGrouped &&
            this.chartStore.groupingStore.swapGroupingAndX
        ) {
            return "grouping";
        }

        return this.chartStore.chartType === "Line" ? "series" : "x";
    }

    /**
     * Grouped stacked/bar charts can only honor chart-level slice ordering when
     * the rule is globally stable across all buckets. Line charts can order by
     * volume because each rendered line has a total across the visible buckets.
     * Slices in a grouped chart cannot honor a volume rule globally because
     * volume totals differ per bucket. When the resolved slice rule is volume,
     * fall back to alphabetical ascending so legend/color order stays stable
     * across charts that share the same grouping dimension.
     */
    private withSliceVolumeAlphabeticalFallback(
        rule: SignalsModels.OrderRule | null | undefined,
    ): SignalsModels.OrderRule | null | undefined {
        if (rule?.orderByValues === "volume") {
            return {
                ...rule,
                orderByValues: "alphabetical",
                orderByDirection: "ascending",
            };
        }
        return rule;
    }

    /**
     * Resolves a grouping-provided order rule using the group-by catalog item
     * and the organization order-rule catalog.
     */
    private getGroupingOrderRule(): SignalsModels.OrderRule | null {
        const groupByOption =
            this.chartStore.groupingStore.activeGrouping?.option ??
            (this.usesFixedGroupingOrderRule()
                ? this.chartStore.config.grouping.fixedGroupingOrderRule
                      ?.groupByOption
                : undefined);
        if (!groupByOption) {
            return null;
        }

        const groupingCatalogEntry =
            this.chartStore.groupingStore.catalogEntryFor(groupByOption);
        if (!groupingCatalogEntry?.orderRuleIdentifier) {
            return null;
        }

        const normalizedRuleIdentifier =
            groupingCatalogEntry.orderRuleIdentifier.trim().toLowerCase();
        if (!normalizedRuleIdentifier.length) {
            return null;
        }

        const matchingOrderRule =
            this.chartStore.optionsStore.signalsOrderRuleCatalog.find(
                (item) =>
                    item.ruleIdentifier?.trim().toLowerCase() ===
                    normalizedRuleIdentifier,
            );
        if (!matchingOrderRule) {
            return null;
        }

        return {
            orderByField: this.getGroupingOrderFieldByChartType(),
            orderByDirection: matchingOrderRule.orderByDirection,
            orderByValues: matchingOrderRule.orderByValues,
        };
    }

    /**
     * Grouped bar charts order axis buckets, while grouped line charts order series.
     */
    private getGroupingOrderFieldByChartType(): string {
        switch (this.chartStore.chartType) {
            case "Line":
                return "series";
            case "VerticalBar":
            case "StackedVerticalBar":
            case "HorizontalBar":
                return "grouping";
            default:
                return "grouping";
        }
    }

    private buildQuestionOrderMap(): Map<string, number> | undefined {
        const module = this.getSelectedModule();
        if (!module?.questions?.length) return undefined;

        const entries = this.flattenQuestions(module.questions).flatMap(
            (question, index) => {
                const labels = [
                    question.shortQuestionText,
                    question.questionText,
                ].filter((label): label is string => !!label?.trim().length);

                return labels.map((label): [string, number] => [
                    this.normalizeOrderingValue(label),
                    index,
                ]);
            },
        );

        return entries.length ? new Map(entries) : undefined;
    }

    private buildQuestionResponseOrderMap(): Map<string, number> | undefined {
        const selectedQuestion = this.getSelectedQuestion();
        if (!selectedQuestion?.tags?.length) return undefined;

        const sortedTags = [...selectedQuestion.tags].sort(
            compareAnswerOptionsByModuleOrder,
        );

        const entries: Array<[string, number]> = [];

        sortedTags.forEach((tag, index) => {
            const normalizedValue = this.normalizeOrderingValue(tag.value);
            if (!normalizedValue.length) return;
            entries.push([normalizedValue, index]);
        });

        return entries.length ? new Map(entries) : undefined;
    }

    /**
     * Orders continuous buckets: date buckets chronologically, numeric ranges low to high.
     *
     * Only identifiers are compared. A bucket's label is written to be read, not sorted; above a
     * daily grain it is the span the bucket covers ("Jan 4 - Jan 10"), which sorts alphabetically
     * into nonsense. The identifiers are built for this: an ISO start date for a date bucket, an
     * ordinal for a range bucket. Numeric collation keeps an unpadded ordinal in order too.
     *
     * A pair with no identifier compares equal, leaving the order the pre-render `bucketOrder`
     * transform already put the rows in.
     */
    private compareBuckets(
        left: OrderableBucket,
        right: OrderableBucket,
        orderByDirection: SignalsModels.OrderByDirection,
    ): number {
        const leftIdentifier = left.identifier?.trim();
        const rightIdentifier = right.identifier?.trim();

        if (!leftIdentifier?.length || !rightIdentifier?.length) {
            return 0;
        }

        const comparison = leftIdentifier.localeCompare(
            rightIdentifier,
            undefined,
            { numeric: true, sensitivity: "base" },
        );

        return orderByDirection === "ascending" ? comparison : -comparison;
    }

    private compareBucketLabelsByOrderMap(
        leftLabel: string,
        rightLabel: string,
        orderMap: Map<string, number> | undefined,
        orderByDirection: SignalsModels.OrderByDirection,
    ): number {
        if (!orderMap?.size) {
            return 0;
        }

        const leftOrder =
            orderMap.get(this.normalizeOrderingValue(leftLabel)) ??
            Number.MAX_SAFE_INTEGER;
        const rightOrder =
            orderMap.get(this.normalizeOrderingValue(rightLabel)) ??
            Number.MAX_SAFE_INTEGER;

        return orderByDirection === "ascending"
            ? leftOrder - rightOrder
            : rightOrder - leftOrder;
    }

    private normalizeOrderingValue(value: unknown): string {
        return `${value ?? ""}`.trim().toLowerCase();
    }

    private getSelectedModule() {
        const moduleId =
            (this.chartStore.selectedIds.module as string | undefined) ??
            this.chartStore.optionsStore.defaultModuleId;

        if (!moduleId) return undefined;

        return this.chartStore.optionsStore.orgModules.find(
            (item) => item.id === moduleId,
        );
    }

    private flattenQuestions(questions?: Question[]): Question[] {
        if (!questions?.length) return [];

        return questions.flatMap((question) => [
            question,
            ...this.flattenQuestions(question.questions),
        ]);
    }

    private getSelectedQuestionId(): string | undefined {
        if (
            typeof this.chartStore.selectedIds.question === "string" &&
            this.chartStore.selectedIds.question.trim().length
        ) {
            return this.chartStore.selectedIds.question;
        }

        if (Array.isArray(this.chartStore.selectedIds.questions)) {
            return this.chartStore.selectedIds.questions.find(
                (id): id is string =>
                    typeof id === "string" && id.trim().length > 0,
            );
        }

        if (
            typeof this.chartStore.selectedIds.questions === "string" &&
            this.chartStore.selectedIds.questions.trim().length
        ) {
            return this.chartStore.selectedIds.questions;
        }

        return undefined;
    }

    /**
     * The module and question a Question Result grouping names, when the chart has one.
     *
     * A chart grouped by question result and a Question Response Count chart both order their
     * answer options by `questionResponseOrder`, but they mean different questions: the grouping's
     * is in its own configuration, not in `selectedIds`. Reading `selectedIds` for a grouped chart
     * would order the bars by the answer options of whichever question the chart itself is scoped
     * to, usually a different list entirely, which leaves every bar unranked and the order
     * arbitrary.
     */
    private getQuestionResultGrouping():
        | { moduleId: string; questionId: string }
        | undefined {
        const grouping = this.chartStore.groupingStore.activeGrouping;

        if (
            grouping?.kind !== "categorical" ||
            grouping.option !== "QuestionResult" ||
            !grouping.licensedModuleId ||
            !grouping.questionId
        ) {
            return undefined;
        }

        return {
            moduleId: grouping.licensedModuleId,
            questionId: grouping.questionId,
        };
    }

    private getSelectedQuestion(): Question | undefined {
        const grouping = this.chartStore.groupingStore.activeGrouping;
        if (
            grouping?.kind === "categorical" &&
            grouping.option === "QuestionResult"
        ) {
            // Never fall through to the chart's own question for a grouped chart, whatever the
            // grouping is missing. `normalizeGroupByConfiguration` guarantees both ids are present,
            // so the undefined branch is unreachable, but the guard keeps the property local rather
            // than resting on a rule three files away.
            const groupedQuestion = this.getQuestionResultGrouping();
            return groupedQuestion
                ? this.chartStore.optionsStore.getQuestionByModuleAndQuestionId(
                      groupedQuestion.moduleId,
                      groupedQuestion.questionId,
                  )
                : undefined;
        }

        const module = this.getSelectedModule();
        const questionId = this.getSelectedQuestionId();

        if (!module?.questions?.length || !questionId) return undefined;

        return this.chartStore.optionsStore.getQuestionByModuleAndQuestionId(
            module.id,
            questionId,
        );
    }
}

export default OrderRulesStore;
